import { createAsync, query, A } from "@solidjs/router";
import { For, Show } from "solid-js";

import { runBatchGetCommand } from "~/_server/dynamoDB";
import { getByRowTypeServer } from "~/_server";
import { formattedTimestamp, padId, schemeData } from "~/lib";

import "~/_assets/admin.css";

// gsiByType is keyed on recordType + sk, and certificate sks come from a
// monotonic counter, so a descending query gives the newest certificates
// without reading the whole type.
const getRecentCertificates = query( async ( limit: number = 10 ) => {
	"use server";

	const result = await getByRowTypeServer(
		'CERTIFICATE',
		limit,
		"pk, sk, riasId, #status, created, scheme, #name, meta.plots, meta.stages, meta.amendments",
		{ '#status': 'status', '#name': 'name' },
		undefined,
		false,
	);

	const certificates = ( result?.items ?? [] ).map( c => ( {
		userId: parseInt( c.pk.split( "#" )[1] ),
		sk: c.sk as string,
		riasId: c.riasId as number | null,
		status: c.status as string,
		created: c.created as number,
		scheme: c.scheme as string,
		name: c.name as string,
		plots: c.meta?.plots?.length ?? 0,
		stage: c.meta?.stages?.length ?? 0,
		amendment: c.meta?.amendments?.length ?? 0,
	} ) );

	if ( !certificates.length ) return { certificates, profiles: new Map<number, any>() };

	const ids = [...new Set( certificates.map( c => c.userId ) )].map( userId => ( {
		pk: `USER#${userId}`,
		sk: `USER#${String( userId ).padStart( 7, "0" )}`,
	} ) );

	const profiles = await runBatchGetCommand( ids, "userId, #name", { "#name": "name" } )
		.then( profiles => new Map<number, any>( profiles?.map( p => [p.userId, p] ) ?? [] ) );

	return { certificates, profiles };
}, "getRecentCertificates" );

export const RecentCertificates = () => {
	const data = createAsync( () => getRecentCertificates( 10 ) );

	return (
		<>
			<h2 class="text-xl text-black font-bold mb-4">Recent Certificates</h2>

			<For each={data()?.certificates}>
				{c => {
					const certifier = () => data()?.profiles?.get( c.userId )?.name;
					const scheme = () => schemeData( c.scheme );

					return (
						<div class="p-3 bg-white rounded-lg shadow-sm mb-2">
							<div class="flex items-start justify-between gap-3">
								<div class="min-w-0">
									<A class="text-sm font-semibold text-gray-900" href={`/dashboard/admin/users/${c.userId}/certificates`}>
										{certifier() ?? `User ${c.userId}`}
									</A>
									<p class="text-sm text-gray-500 truncate" title={c.name}>
										<Show when={c.riasId} fallback={<span class="font-mono text-xs">unnumbered</span>}>
											<span class="font-mono text-xs">C{padId( c.riasId! )}</span>
										</Show>
										<Show when={c.stage}> · Stage {c.stage}</Show>
										<Show when={c.amendment}> · Amendment {c.amendment}</Show>
										{" · "}{c.name}
									</p>
								</div>
								<span class={`cert-status shrink-0 capitalize ${c.status}`}>{c.status}</span>
							</div>

							<div class="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
								<span title={scheme()?.name ?? c.scheme}>{scheme()?.shortName ?? c.scheme}</span>
								<span>{c.plots} plot{c.plots === 1 ? '' : 's'}</span>
								<span class="ml-auto">{formattedTimestamp( c.created )}</span>
							</div>
						</div>
					);
				}}
			</For>
		</>
	);
};
